import React from "react";
import "./css/FormComponents.css";

/**
 * InputField Component
 * A reusable input field component with consistent styling and behavior
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.label - Label text
 * @param {string} props.value - Input value
 * @param {function} props.onChange - Change handler function
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.className - Additional CSS classes to apply
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.error - Error message to display
 * @param {Object} props.autoComplete - Autocomplete attribute
 * @param {React.ReactNode} props.prefix - Content to show before the input
 * @param {React.ReactNode} props.suffix - Content to show after the input
 * @returns {React.ReactElement} InputField component
 */
const InputField = ({
  type = "text",
  name,
  id = name,
  label,
  value,
  onChange,
  placeholder,
  required = false,
  className = "",
  disabled = false,
  error,
  autoComplete,
  prefix,
  suffix,
  ...rest
}) => {
  return (
    <div className={`input-field-container ${className}`}>
      {label && (
        <label htmlFor={id} className="input-label">
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}

      <div className="input-wrapper">
        {prefix && <div className="input-prefix">{prefix}</div>}

        {type === "textarea" ? (
          <textarea
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={`input-field textarea ${error ? "input-error" : ""}`}
            autoComplete={autoComplete}
            {...rest}
          />
        ) : (
          <input
            type={type}
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={`input-field ${error ? "input-error" : ""}`}
            autoComplete={autoComplete}
            {...rest}
          />
        )}

        {suffix && <div className="input-suffix">{suffix}</div>}
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default InputField;
